import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Search, Users } from "lucide-react";

import {
    bulkUpdateEventAttendanceRequest,
    fetchEventAttendanceRequest,
} from "../../../../../redux/actions/eventAttendanceActions";
import { fetchEventsRequest } from "../../../../../redux/actions/eventsActions";
import "../../RessourcesStyles/EventAbsenceManagement.css";

const STATUS_OPTIONS = [
    { value: "ALL", label: "Tous les statuts" },
    { value: "NOT_SET", label: "Non renseigne" },
    { value: "PRESENT", label: "Present" },
    { value: "ABSENT", label: "Absent" },
];

const statusBadgeClass = {
    PRESENT: "ev-badge ev-badge--soft-green",
    ABSENT: "ev-badge ev-badge--soft-red",
    NOT_SET: "ev-badge ev-badge--grey",
};

const statusLabel = {
    PRESENT: "Present",
    ABSENT: "Absent",
    NOT_SET: "Non renseigne",
};

const eventStatusStyle = {
    PLANIFIE: { bg: "#e8edf7", color: "#204385", label: "Planifié" },
    EN_COURS: { bg: "#d1fae5", color: "#059669", label: "En cours" },
    TERMINE: { bg: "#f3f4f6", color: "#6b7280", label: "Terminé" },
    ANNULE: { bg: "#fde8e7", color: "#ee645f", label: "Annulé" },
};

const normalize = (value) => String(value || "").trim().toLowerCase();

const emptyBulkForm = {
    status: "ABSENT",
    absenceReason: "",
    comment: "",
};

export default function EventAbsenceManagementPage() {
    const { eventId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const numericEventId = Number(eventId);
    const eventsState = useSelector((state) => state.events || {});
    const attendanceState = useSelector((state) => state.eventAttendance || {});

    const events = eventsState.events || [];
    const loading = Boolean(eventsState.loading || attendanceState.loading);
    const submitting = Boolean(attendanceState.submitting);

    const [searchTerm, setSearchTerm] = useState("");
    const [posteFilter, setPosteFilter] = useState("ALL");
    const [entiteFilter, setEntiteFilter] = useState("ALL");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkForm, setBulkForm] = useState(emptyBulkForm);
    const [rows, setRows] = useState([]);

    const event = useMemo(
        () => events.find((item) => Number(item.id) === numericEventId) || null,
        [events, numericEventId]
    );
    const sessions = useMemo(() => Array.isArray(event?.sessions) ? event.sessions : [], [event]);
    const [selectedSessionId, setSelectedSessionId] = useState(null);
    const attendanceKey = selectedSessionId ? `${numericEventId}:${selectedSessionId}` : null;
    const attendanceRows = attendanceKey ? (attendanceState.byEventSession?.[attendanceKey] || []) : [];

    useEffect(() => {
        if (!events.length) {
            dispatch(fetchEventsRequest());
        }
    }, [dispatch, events.length]);

    useEffect(() => {
        if (!sessions.length) return;
        setSelectedSessionId((current) => {
            if (current && sessions.some((session) => Number(session.id) === Number(current))) {
                return current;
            }
            return sessions[0]?.id || null;
        });
    }, [sessions]);

    useEffect(() => {
        if (numericEventId && selectedSessionId) {
            dispatch(fetchEventAttendanceRequest(numericEventId, selectedSessionId));
        }
    }, [dispatch, numericEventId, selectedSessionId]);

    useEffect(() => {
        setRows(attendanceRows);
        setSelectedIds(new Set());
    }, [attendanceRows, selectedSessionId]);

    useEffect(() => {
        if (attendanceState.lastUpdatedEventId === attendanceKey) {
            setSelectedIds(new Set());
        }
    }, [attendanceState.lastUpdatedEventId, attendanceKey]);

    const posteOptions = useMemo(
        () => ["ALL", ...new Set(rows.map((row) => row.poste).filter(Boolean))],
        [rows]
    );

    const entiteOptions = useMemo(
        () => ["ALL", ...new Set(rows.map((row) => row.entite).filter(Boolean))],
        [rows]
    );

    const filteredRows = useMemo(() => {
        return rows.filter((row) => {
            const matchesSearch =
                !searchTerm
                || normalize(row.fullName).includes(normalize(searchTerm))
                || normalize(row.email).includes(normalize(searchTerm));
            const matchesPoste = posteFilter === "ALL" || row.poste === posteFilter;
            const matchesEntite = entiteFilter === "ALL" || row.entite === entiteFilter;
            const matchesStatus = statusFilter === "ALL" || row.status === statusFilter;
            return matchesSearch && matchesPoste && matchesEntite && matchesStatus;
        });
    }, [rows, searchTerm, posteFilter, entiteFilter, statusFilter]);

    const allFilteredSelected =
        filteredRows.length > 0
        && filteredRows.every((row) => selectedIds.has(row.userId));

    const handleToggleSelection = (userId) => {
        setSelectedIds((current) => {
            const next = new Set(current);
            if (next.has(userId)) {
                next.delete(userId);
            } else {
                next.add(userId);
            }
            return next;
        });
    };

    const handleToggleAllFiltered = () => {
        setSelectedIds((current) => {
            const next = new Set(current);
            if (allFilteredSelected) {
                filteredRows.forEach((row) => next.delete(row.userId));
            } else {
                filteredRows.forEach((row) => next.add(row.userId));
            }
            return next;
        });
    };

    const handleRowChange = (userId, field, value) => {
        setRows((current) =>
            current.map((row) => {
                if (row.userId !== userId) return row;

                if (field === "status") {
                    const nextStatus = value;
                    return {
                        ...row,
                        status: nextStatus,
                        absenceReason: nextStatus === "ABSENT" ? row.absenceReason || "" : "",
                        comment: nextStatus === "ABSENT" ? row.comment || "" : "",
                    };
                }

                return {
                    ...row,
                    [field]: value,
                };
            })
        );
    };

    const applyBulkStatus = () => {
        if (!selectedIds.size) return;

        setRows((current) =>
            current.map((row) => {
                if (!selectedIds.has(row.userId)) return row;

                if (bulkForm.status === "ABSENT") {
                    return {
                        ...row,
                        status: "ABSENT",
                        absenceReason: bulkForm.absenceReason || row.absenceReason || "",
                        comment: bulkForm.comment || row.comment || "",
                    };
                }

                return {
                    ...row,
                    status: bulkForm.status,
                    absenceReason: "",
                    comment: "",
                };
            })
        );
    };

    const handleSave = () => {
        const selectedRows = rows.filter((row) => selectedIds.has(row.userId));
        if (!selectedRows.length) return;

        dispatch(
            bulkUpdateEventAttendanceRequest(
                numericEventId,
                selectedSessionId,
                selectedRows.map((row) => ({
                    userId: row.userId,
                    sessionId: selectedSessionId,
                    status: row.status,
                    absenceReason: row.status === "ABSENT" ? row.absenceReason : null,
                    comment: row.status === "ABSENT" ? row.comment : null,
                }))
            )
        );
    };

    const statusView = eventStatusStyle[event?.status] || eventStatusStyle.PLANIFIE;

    return (
        <div className="ev-page">
            <div className="ev-header">
                <div className="ev-header-info">
                    <h2>Gestion des absences</h2>
                </div>
                <div className="ev-header-btns">
                    <button
                        type="button"
                        className="ev-btn ev-btn-light ea-back-btn"
                        onClick={() => navigate("/formation/responsable/evenements")}
                    >
                        <ArrowLeft size={14} />
                        Retour aux événements
                    </button>
                </div>
            </div>

            <div className="ea-event-summary">
                <div className="ev-box">
                    <span className="ea-summary-label">Titre</span>
                    <div className="ea-summary-value">{event?.titre || `Evenement #${numericEventId}`}</div>
                </div>
                <div className="ev-box">
                    <span className="ea-summary-label">Date de début</span>
                    <div className="ea-summary-value">{event?.dateDebut?.slice(0, 16) || "-"}</div>
                </div>
                <div className="ev-box">
                    <span className="ea-summary-label">Date de fin</span>
                    <div className="ea-summary-value">{event?.dateFin?.slice(0, 16) || "-"}</div>
                </div>
                <div className="ev-box">
                    <span className="ea-summary-label">Lieu</span>
                    <div className="ea-summary-value">{event?.offsiteLocation || event?.salle?.nom || "-"}</div>
                </div>
                <div className="ev-box">
                    <span className="ea-summary-label">Statut</span>
                    <div className="ea-summary-value">
                        <span className="ev-badge" style={{ background: statusView.bg, color: statusView.color }}>
                            {statusView.label}
                        </span>
                    </div>
                </div>
            </div>

            <div className="ev-toolbar">
                <div className="ea-toolbar-grid">
                    <div className="ea-toolbar-field">
                        <label>Seance</label>
                        <select
                            value={selectedSessionId || ""}
                            onChange={(eventTarget) => setSelectedSessionId(Number(eventTarget.target.value))}
                            disabled={!sessions.length}
                        >
                            {sessions.map((session) => (
                                <option key={session.id} value={session.id}>
                                    {session.name || "Seance"} - {session.startat?.slice(0, 16) || session.endat?.slice(0, 16) || session.id}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="ea-toolbar-field">
                        <label>Recherche</label>
                        <div className="ev-search-wrap" style={{ width: "100%" }}>
                            <Search size={14} className="ev-search-icon" />
                            <input
                                type="text"
                                className="ev-search"
                                placeholder="Nom ou email"
                                value={searchTerm}
                                onChange={(eventTarget) => setSearchTerm(eventTarget.target.value)}
                            />
                        </div>
                    </div>

                    <div className="ea-toolbar-field">
                        <label>Poste</label>
                        <select value={posteFilter} onChange={(eventTarget) => setPosteFilter(eventTarget.target.value)}>
                            {posteOptions.map((option) => (
                                <option key={option} value={option}>
                                    {option === "ALL" ? "Tous les postes" : option}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="ea-toolbar-field">
                        <label>Entite</label>
                        <select value={entiteFilter} onChange={(eventTarget) => setEntiteFilter(eventTarget.target.value)}>
                            {entiteOptions.map((option) => (
                                <option key={option} value={option}>
                                    {option === "ALL" ? "Toutes les entites" : option}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="ea-toolbar-field">
                        <label>Presence</label>
                        <select value={statusFilter} onChange={(eventTarget) => setStatusFilter(eventTarget.target.value)}>
                            {STATUS_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="ev-card">
                <div className="ev-box" style={{ margin: "14px 18px 0" }}>
                    <p className="ev-section-title">Actions massives</p>
                    <div className="ea-bulk-grid">
                        <div className="ev-field">
                            <label>Statut a appliquer</label>
                            <select
                                value={bulkForm.status}
                                onChange={(eventTarget) =>
                                    setBulkForm((current) => ({ ...current, status: eventTarget.target.value }))
                                }
                            >
                                <option value="PRESENT">Marquer Present</option>
                                <option value="ABSENT">Marquer Absent</option>
                                <option value="NOT_SET">Remettre a Non renseigne</option>
                            </select>
                        </div>

                        <div className="ev-field">
                            <label>Motif commun</label>
                            <input
                                type="text"
                                className="ev-input"
                                value={bulkForm.absenceReason}
                                onChange={(eventTarget) =>
                                    setBulkForm((current) => ({ ...current, absenceReason: eventTarget.target.value }))
                                }
                                disabled={bulkForm.status !== "ABSENT"}
                                placeholder="Ex: Maladie"
                            />
                        </div>

                        <div className="ev-field">
                            <label>Commentaire commun</label>
                            <input
                                type="text"
                                className="ev-input"
                                value={bulkForm.comment}
                                onChange={(eventTarget) =>
                                    setBulkForm((current) => ({ ...current, comment: eventTarget.target.value }))
                                }
                                disabled={bulkForm.status !== "ABSENT"}
                                placeholder="Commentaire optionnel"
                            />
                        </div>

                        <div className="ea-bulk-actions">
                            <button
                                type="button"
                                className="ev-btn ev-btn-light"
                                onClick={applyBulkStatus}
                                disabled={!selectedIds.size}
                            >
                                Appliquer à la sélection
                            </button>
                            <button
                                type="button"
                                className="ev-btn ev-btn-primary"
                                onClick={handleSave}
                                disabled={!selectedIds.size || submitting}
                            >
                                {submitting ? "Enregistrement..." : "Enregistrer"}
                            </button>
                        </div>
                    </div>
                </div>

                <div className="ea-table-meta">
                    <strong>
                        <Users size={14} style={{ marginRight: 6, verticalAlign: "text-bottom" }} />
                        {filteredRows.length} collaborateur(s)
                    </strong>
                    <span className="ea-helper">
                        {selectedSessionId ? `Seance #${selectedSessionId} · ` : ""}
                        {selectedIds.size} selectionne(s) pour l'enregistrement
                    </span>
                </div>

                <div className="ev-table-wrap">
                    <table className="ev-table">
                        <thead>
                            <tr>
                                <th className="ea-select-cell">
                                    <input
                                        type="checkbox"
                                        checked={allFilteredSelected}
                                        onChange={handleToggleAllFiltered}
                                    />
                                </th>
                                <th>Collaborateur</th>
                                <th>Email</th>
                                <th>Poste</th>
                                <th>Entite</th>
                                <th>Presence</th>
                                <th>Motif</th>
                                <th>Commentaire</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!loading && filteredRows.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="ea-empty">
                                        Aucun collaborateur ne correspond aux filtres actuels.
                                    </td>
                                </tr>
                            )}

                            {filteredRows.map((row) => (
                                <tr key={row.assignmentId || row.userId}>
                                    <td className="ea-select-cell">
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(row.userId)}
                                            onChange={() => handleToggleSelection(row.userId)}
                                        />
                                    </td>
                                    <td>
                                        <div style={{ fontWeight: 700 }}>{row.fullName || "-"}</div>
                                        <div className="ev-hint">{row.markedAt ? `Maj: ${row.markedAt.slice(0, 16)}` : "Aucune mise a jour"}</div>
                                    </td>
                                    <td>{row.email || "-"}</td>
                                    <td>{row.poste || "-"}</td>
                                    <td>{row.entite || "-"}</td>
                                    <td>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                            <span className={statusBadgeClass[row.status] || statusBadgeClass.NOT_SET}>
                                                {statusLabel[row.status] || statusLabel.NOT_SET}
                                            </span>
                                            <select
                                                className="ea-status-select"
                                                value={row.status || "NOT_SET"}
                                                onChange={(eventTarget) =>
                                                    handleRowChange(row.userId, "status", eventTarget.target.value)
                                                }
                                            >
                                                <option value="NOT_SET">Non renseigne</option>
                                                <option value="PRESENT">Present</option>
                                                <option value="ABSENT">Absent</option>
                                            </select>
                                        </div>
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            className="ea-comment-input"
                                            value={row.absenceReason || ""}
                                            onChange={(eventTarget) =>
                                                handleRowChange(row.userId, "absenceReason", eventTarget.target.value)
                                            }
                                            disabled={row.status !== "ABSENT"}
                                            placeholder="Motif optionnel"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            className="ea-comment-input"
                                            value={row.comment || ""}
                                            onChange={(eventTarget) =>
                                                handleRowChange(row.userId, "comment", eventTarget.target.value)
                                            }
                                            disabled={row.status !== "ABSENT"}
                                            placeholder="Commentaire optionnel"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
